import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { marked } from 'marked';
import { useApi } from '@/hooks/useApi';
import { showToast } from '@/components/layout/Toast';
import { sanitizeHtml } from '@/lib/utils';
import { PageHeader, Breadcrumbs } from '@/components/ui'
import type {
  BlogArticle, BlogArticleListItem, BlogFaqItem, BlogFrontmatter,
} from '@/lib/types';

/* Claves válidas de relatedProduct / [[producto:X]] — deben coincidir con blog-lib.mjs del repo web */
const PRODUCTS: { key: string; label: string }[] = [
  { key: 'hosting', label: 'Hosting' },
  { key: 'monday', label: 'Monday' },
  { key: 'consultoria', label: 'Consultoría' },
  { key: 'desarrollo', label: 'Desarrollo' },
  { key: 'soporte', label: 'Soporte' },
  { key: 'workspace', label: 'Google Workspace' },
  { key: 'cloudflare', label: 'Cloudflare' },
  { key: 'servidores', label: 'Servidores' },
  { key: 'leadforge', label: 'LeadForge' },
];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .trim();
}

const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY_FM: BlogFrontmatter = {
  title: '', description: '', date: TODAY, author: 'José Castañeda',
  authorAvatar: null, cover: null, tags: [], draft: true, featured: false,
  relatedProduct: null, keyTakeaways: [], faq: [],
};

export default function Blog() {
  // Métodos estables de useApi (el objeto que devuelve el hook cambia de identidad
  // en cada render; depender de él en useCallback/useEffect causa un loop de fetch).
  const { get, post, put, del, upload } = useApi();

  const [view, setView] = useState<'list' | 'edit'>('list');
  const [articulos, setArticulos] = useState<BlogArticleListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  // ── Editor state ──
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [sha, setSha] = useState<string | null>(null);
  const [isNew, setIsNew] = useState(true);
  const [fm, setFm] = useState<BlogFrontmatter>(EMPTY_FM);
  const [body, setBody] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const imgInputRef = useRef<HTMLInputElement | null>(null);

  const patchFm = (p: Partial<BlogFrontmatter>) => setFm((prev) => ({ ...prev, ...p }));

  // ── Fetch list ──
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get<{ articulos: BlogArticleListItem[] }>('/api/blog');
      setArticulos(data.articulos || []);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al cargar artículos', 'error');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => { fetchList(); }, [fetchList]);

  // ── Auto-slug desde el título (solo en artículo nuevo y si no fue tocado) ──
  useEffect(() => {
    if (isNew && !slugTouched) setSlug(slugify(fm.title));
  }, [fm.title, isNew, slugTouched]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return articulos;
    return articulos.filter((a) =>
      a.title.toLowerCase().includes(term) || a.slug.toLowerCase().includes(term));
  }, [articulos, q]);

  // ── Editor navigation ──
  const openNew = () => {
    setIsNew(true); setSlug(''); setSlugTouched(false); setSha(null);
    setFm({ ...EMPTY_FM, date: TODAY }); setBody(''); setTagsInput('');
    setView('edit');
  };

  const openEdit = async (item: BlogArticleListItem) => {
    setLoading(true);
    try {
      const art = await get<BlogArticle>(`/api/blog/${item.slug}`);
      setIsNew(false); setSlug(art.slug); setSlugTouched(true); setSha(art.sha);
      setFm({ ...EMPTY_FM, ...art.frontmatter });
      setBody(art.body || '');
      setTagsInput((art.frontmatter.tags || []).join(', '));
      setView('edit');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al abrir el artículo', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item: BlogArticleListItem) => {
    if (!confirm(`¿Eliminar el artículo "${item.title}"? Se borrará el .md del repo.`)) return;
    try {
      await del(`/api/blog/${item.slug}`);
      showToast('Artículo eliminado', 'success');
      fetchList();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al eliminar', 'error');
    }
  };

  // ── Uploads ──
  const uploadCover = async (file: File) => {
    const s = slug || slugify(fm.title);
    if (!s) { showToast('Escribe primero el título/slug', 'error'); return; }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('slug', s);
      const res = await upload<{ url: string }>('/api/blog/cover', fd);
      patchFm({ cover: res.url });
      showToast('Portada subida', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al subir la portada', 'error');
    } finally {
      setUploadingCover(false);
    }
  };

  const uploadBodyImage = async (file: File) => {
    const s = slug || slugify(fm.title);
    if (!s) { showToast('Escribe primero el título/slug', 'error'); return; }
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('slug', s);
      const res = await upload<{ url: string }>('/api/blog/imagen', fd);
      insertAtCursor(`![imagen](${res.url})`);
      showToast('Imagen insertada', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al subir la imagen', 'error');
    } finally {
      setUploadingImg(false);
    }
  };

  // ── Insert en el textarea del cuerpo ──
  const insertAtCursor = (text: string) => {
    const ta = bodyRef.current;
    if (!ta) { setBody((b) => b + text); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = body.substring(0, start) + text + body.substring(end);
    setBody(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + text.length, start + text.length);
    });
  };

  // ── keyTakeaways / faq helpers ──
  const setTakeaway = (i: number, val: string) =>
    patchFm({ keyTakeaways: fm.keyTakeaways.map((k, idx) => (idx === i ? val : k)) });
  const addTakeaway = () => patchFm({ keyTakeaways: [...fm.keyTakeaways, ''] });
  const removeTakeaway = (i: number) =>
    patchFm({ keyTakeaways: fm.keyTakeaways.filter((_, idx) => idx !== i) });

  const setFaq = (i: number, key: keyof BlogFaqItem, val: string) =>
    patchFm({ faq: fm.faq.map((f, idx) => (idx === i ? { ...f, [key]: val } : f)) });
  const addFaq = () => patchFm({ faq: [...fm.faq, { q: '', a: '' }] });
  const removeFaq = (i: number) => patchFm({ faq: fm.faq.filter((_, idx) => idx !== i) });

  // ── Preview ──
  const previewHtml = useMemo(() => {
    if (!body.trim()) return '';
    const withCallouts = body.replace(/^\s*\[\[producto:([a-z]+)\]\]\s*$/gim,
      (_m, key) => `\n> 📦 **Producto relacionado:** \`${key}\`\n`);
    return sanitizeHtml(marked.parse(withCallouts, { gfm: true, breaks: true }) as string);
  }, [body]);

  // ── Save ──
  const handleSave = async () => {
    if (!fm.title.trim()) { showToast('El título es obligatorio', 'error'); return; }
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
      showToast('Slug inválido (minúsculas, números y guiones)', 'error'); return;
    }
    setSaving(true);
    try {
      const tags = tagsInput.split(',').map((t) => t.trim()).filter(Boolean);
      const payload = { ...fm, tags, slug, body };
      if (isNew) {
        const res = await post<{ ok: boolean; sha: string }>('/api/blog/publicar', payload);
        setIsNew(false); setSha(res.sha);
      } else {
        const res = await put<{ ok: boolean; sha: string }>(`/api/blog/${slug}`, { ...payload, sha });
        setSha(res.sha);
      }
      showToast(
        fm.draft
          ? 'Guardado como borrador (no se publica en producción)'
          : 'Publicado — Cloudflare Pages reconstruirá en 1-2 min',
        'success',
      );
      fetchList();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al guardar', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ── LIST VIEW ── */
  if (view === 'list') {
    return (
      <div className="page-enter">
        <PageHeader
          breadcrumbs={[{ label: 'Marketing' }, { label: 'Blog' }]}
          subtitle={<>Publicar escribe el <code>.md</code> en el repo web: el sitio es estático.</>}
          actions={
            <>
              <button className="btn btn-secondary" onClick={fetchList}>Refrescar</button>
              <button className="btn btn-primary" onClick={openNew}>+ Nuevo artículo</button>
            </>
          }
        />

        <div className="toolbar" style={{ marginBottom: 16 }}>
          <input
            className="form-input"
            style={{ maxWidth: 340 }}
            placeholder="Buscar por título o slug…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Título</th>
                <th>Slug</th>
                <th>Fecha</th>
                <th>Producto</th>
                <th>Estado</th>
                <th style={{ textAlign: 'right' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  Sin artículos. Crea el primero con “+ Nuevo artículo”.
                </td></tr>
              ) : (
                filtered.map((a) => (
                  <tr key={a.slug}>
                    <td style={{ fontWeight: 600 }}>{a.title}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{a.slug}</td>
                    <td>{a.date || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td>{a.relatedProduct
                      ? <span className="badge badge-producto">{a.relatedProduct}</span>
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td>
                      {a.draft
                        ? <span className="badge badge-borrador">Borrador</span>
                        : <span className="badge badge-publicado">Publicado</span>}
                      {a.featured && <span className="badge badge-destacado" style={{ marginLeft: 6 }}>Destacado</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(a)}>Editar</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(a)}>Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ── EDIT VIEW ── */
  return (
    <div className="page-enter">
      <div className="page-header">
        <div>
          <Breadcrumbs items={[
            { label: 'Blog', onClick: () => setView('list') },
            { label: isNew ? 'Nuevo artículo' : slug },
          ]} />
        </div>
        <div className="page-actions" style={{ alignItems: 'center' }}>
          <label className="form-check">
            <input type="checkbox" checked={fm.draft} onChange={(e) => patchFm({ draft: e.target.checked })} />
            Borrador
          </label>
          <label className="form-check">
            <input type="checkbox" checked={fm.featured} onChange={(e) => patchFm({ featured: e.target.checked })} />
            Destacado
          </label>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : isNew ? 'Publicar' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 20, alignItems: 'start' }}>
        {/* ── Columna izquierda: cuerpo ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="form-group">
              <label className="form-label">Título</label>
              <input className="form-input" value={fm.title} onChange={(e) => patchFm({ title: e.target.value })} placeholder="Título del artículo" />
            </div>

            <div className="form-group">
              <label className="form-label">Slug (URL)</label>
              <input
                className="form-input"
                value={slug}
                disabled={!isNew}
                onChange={(e) => { setSlugTouched(true); setSlug(slugify(e.target.value)); }}
                placeholder="mi-articulo"
              />
              {!isNew && <div className="form-hint">El slug no se puede cambiar al editar (es el nombre del archivo).</div>}
            </div>

            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Descripción (meta, 150-160 caracteres)</label>
              <textarea
                className="form-textarea"
                style={{ minHeight: 60 }}
                value={fm.description}
                onChange={(e) => patchFm({ description: e.target.value })}
                placeholder="Meta description para SEO"
              />
              <div className="form-hint" style={{ color: fm.description.length > 160 ? 'var(--danger)' : undefined }}>
                {fm.description.length} caracteres
              </div>
            </div>
          </div>

          {/* Editor de cuerpo */}
          <div className="card">
            <div className="toolbar" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <label className="form-label" style={{ margin: 0 }}>Cuerpo (Markdown)</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select
                  className="form-select"
                  value=""
                  onChange={(e) => { if (e.target.value) { insertAtCursor(`\n\n[[producto:${e.target.value}]]\n\n`); e.target.value = ''; } }}
                >
                  <option value="">+ Insertar producto…</option>
                  {PRODUCTS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
                <button className="btn btn-sm btn-secondary" disabled={uploadingImg} onClick={() => imgInputRef.current?.click()}>
                  {uploadingImg ? 'Subiendo…' : '+ Imagen'}
                </button>
                <input
                  ref={imgInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadBodyImage(f); e.target.value = ''; }}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <textarea
                ref={bodyRef}
                className="form-textarea"
                style={{ minHeight: 460, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.82rem', lineHeight: 1.6 }}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Escribe el artículo en Markdown…"
                spellCheck={false}
              />
              <div
                className="wiki-prose"
                style={{ minHeight: 460, maxHeight: 600, overflowY: 'auto', padding: 16, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}
                dangerouslySetInnerHTML={{ __html: previewHtml || '<p style="opacity:.5">La vista previa aparecerá aquí…</p>' }}
              />
            </div>
          </div>
        </div>

        {/* ── Columna derecha: metadata ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <label className="form-label">Portada</label>
            {fm.cover ? (
              <div>
                <img src={fm.cover} alt="portada" style={{ width: '100%', borderRadius: 'var(--radius-md)', marginBottom: 8, display: 'block' }} />
                <button className="btn btn-sm btn-secondary" onClick={() => patchFm({ cover: null })}>Quitar</button>
              </div>
            ) : (
              <div className="form-hint" style={{ marginBottom: 8 }}>Sin portada (recomendado 1600×900 .webp)</div>
            )}
            <label className="btn btn-sm btn-secondary" style={{ marginTop: 8, cursor: 'pointer' }}>
              {uploadingCover ? 'Subiendo…' : fm.cover ? 'Reemplazar portada' : 'Subir portada'}
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadCover(f); e.target.value = ''; }} />
            </label>
          </div>

          <div className="card">
            <div className="form-group">
              <label className="form-label">Fecha</label>
              <input type="date" className="form-input" value={fm.date} onChange={(e) => patchFm({ date: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Autor</label>
              <input className="form-input" value={fm.author} onChange={(e) => patchFm({ author: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Producto relacionado</label>
              <select className="form-select" value={fm.relatedProduct || ''} onChange={(e) => patchFm({ relatedProduct: e.target.value || null })}>
                <option value="">— Ninguno —</option>
                {PRODUCTS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Tags (separados por coma)</label>
              <input className="form-input" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} placeholder="Hosting, Infraestructura" />
            </div>
          </div>

          <div className="card">
            <div className="toolbar" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <label className="form-label" style={{ margin: 0 }}>Key takeaways</label>
              <button className="btn btn-sm btn-secondary" onClick={addTakeaway}>+ Añadir</button>
            </div>
            {fm.keyTakeaways.length === 0 && <div className="form-hint">Frases citables (opcional).</div>}
            {fm.keyTakeaways.map((k, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <input className="form-input" style={{ flex: 1 }} value={k} onChange={(e) => setTakeaway(i, e.target.value)} placeholder="Frase citable" />
                <button className="btn btn-sm btn-danger" onClick={() => removeTakeaway(i)}>×</button>
              </div>
            ))}
          </div>

          <div className="card">
            <div className="toolbar" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
              <label className="form-label" style={{ margin: 0 }}>FAQ</label>
              <button className="btn btn-sm btn-secondary" onClick={addFaq}>+ Añadir</button>
            </div>
            {fm.faq.length === 0 && <div className="form-hint">Preguntas frecuentes (genera JSON-LD FAQPage).</div>}
            {fm.faq.map((f, i) => (
              <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 10, marginBottom: 10 }}>
                <input className="form-input" style={{ marginBottom: 6 }} value={f.q} onChange={(e) => setFaq(i, 'q', e.target.value)} placeholder="¿Pregunta?" />
                <textarea className="form-textarea" style={{ minHeight: 50 }} value={f.a} onChange={(e) => setFaq(i, 'a', e.target.value)} placeholder="Respuesta" />
                <button className="btn btn-sm btn-danger" style={{ marginTop: 6 }} onClick={() => removeFaq(i)}>Quitar</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
